import * as Sentry from '@sentry/react';
import NiceModal from '@ebay/nice-modal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';
import type { ErrorInfo } from 'react';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useDispatch, useSelector } from 'react-redux';
import { setupAxiosInterceptors } from '@/api/interceptors';
import ErrorFallBack from '@/components/ErrorFallBack';
import Loading from '@/components/UI/loading';
import { isPublicAuthOptionalPath } from '@/route/publicRoutePaths';
import { authSelector } from '@/store';
import { getCurrentAccountInfo } from '@/store/Account';
import { tryCurrentUser } from '@/store/Auth';
import Allroute from './route';

axios.defaults.withCredentials = true;

const _host = window.location.host;
let host = '';
if (_host.match(/lvh.me/)) {
  host = 'lvh.me:3000';
} else if (_host.match(/localhost:3000/)) {
  host = 'localhost:3000';
} else if (_host.match(/sageterminal.com/)) {
  host = 'sageterminal.com';
} else if (_host.match(/seinterminal.com/)) {
  host = 'seinterminal.com';
} else if (_host.match(/seinterminal.dev/)) {
  host = 'seinterminal.dev';
} else if (_host.match(/sagetechnologypartners.com/)) {
  host = 'sagetechnologypartners.com';
}
const subSplits = _host.split('.');
let sub: string;

if (host === 'lvh.me:3000') {
  host = 'api.lvh.me:7070';
} else if (host === 'localhost:3000') {
  host = 'localhost:7070';
} else if (subSplits.length > 2) {
  sub = subSplits[0];
  host = `${sub}.api.${host}`;
} else {
  host = `api.${host}`;
}

axios.defaults.baseURL = `//${host}`;

const xSubdomain = localStorage.getItem('xSubdomain');
if (xSubdomain) {
  axios.defaults.headers.common['x-subdomain'] = xSubdomain;
}

const errorHandler = (error: unknown, errorInfo: ErrorInfo) => {
  Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
};

const queryClient = new QueryClient();

// Setup axios interceptors on app initialization
setupAxiosInterceptors(axios);

export const App = () => {
  const dispatch = useDispatch();
  const { loading, isLoggedIn, data: userData } = useSelector(authSelector);
  const prevLoggedIn = useRef(isLoggedIn);

  useEffect(() => {
    if (isLoggedIn && userData?.id) {
      Sentry.setUser({
        id: String(userData.id),
        email: userData.email,
        username: userData.name,
      });
      if (!prevLoggedIn.current) {
        Sentry.logger.info('User session started', { user_id: String(userData.id) });
        Sentry.metrics.count('user.logins', 1);
      }
    } else {
      if (prevLoggedIn.current) {
        Sentry.logger.info('User session ended');
        Sentry.metrics.count('user.logouts', 1);
      }
      Sentry.setUser(null);
    }
    prevLoggedIn.current = isLoggedIn;
  }, [isLoggedIn, userData?.id, userData?.email, userData?.name]);

  useLayoutEffect(() => {
    // Skip auth for public routes
    const isPublicRoute = isPublicAuthOptionalPath(window.location.pathname);

    if (!isPublicRoute) {
      dispatch(tryCurrentUser() as any);
      if (isLoggedIn) {
        dispatch(getCurrentAccountInfo() as any);
      }
    }
  }, [isLoggedIn, dispatch]);

  // Enforce user-type redirects based on isSuper value from current user
  // NOTE: This is now handled by route guards (RequireSystemAdmin, RequireTenantUser)
  // and the root redirect in Home.tsx, so we don't need this here
  // Keeping the code but commented out to avoid conflicts with route-level redirects
  /*
  const { data: userData } = useSelector(authSelector);
  useLayoutEffect(() => {
    if (!userData?.id) return; // User not loaded yet

    const currentPath = window.location.pathname;
    const isSuper = userData.isSuper;

    // System Admin (isSuper=true) should not access /app routes
    if (isSuper && currentPath.startsWith('/app/')) {
      window.location.href = '/os/clients';
      return;
    }

    // Tenant User (isSuper=false) should not access /os routes
    if (!isSuper && currentPath.startsWith('/os/')) {
      window.location.href = '/terminal/dashboard';
      return;
    }
  }, [userData?.id, userData?.isSuper]);
  */

  if (loading) {
    return <Loading loadingText='Loading' />;
  }

  return (
    <ErrorBoundary fallback={<ErrorFallBack />} onError={errorHandler}>
      <QueryClientProvider client={queryClient}>
        <NiceModal.Provider>
          <Allroute />
        </NiceModal.Provider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};
